import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Anchor, Flag, Phone, Search } from 'lucide-react';
import { FaInstagram, FaFacebook, FaTwitter, FaYoutube, FaLinkedin } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import SearchBar from './SearchBar';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  const navItems = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('about') },
    { path: '/products', label: t('products') },
    { path: '/media', label: t('media') },
    { path: '/contact', label: t('contact') }
  ];

  const isActive = (path: string) => location.pathname === path;

    const socialLinks = [
    { icon: FaInstagram, href: 'https://www.instagram.com/aymersafety/', color: 'hover:text-pink-500' },
    { icon: FaLinkedin, href: 'https://www.linkedin.com/public-profile/settings?trk=d_flagship3_profile_self_view_public_profile', color: 'hover:text-blue-700' },
    { icon: FaFacebook, href: 'https://www.instagram.com/aymersafety/', color: 'hover:text-blue-600' }, 
    { icon: FaTwitter, href: 'https://www.instagram.com/aymersafety/', color: 'hover:text-blue-400' },
    { icon: FaYoutube, href: 'https://www.instagram.com/aymersafety/', color: 'hover:text-red-600' }
  ];
  return (
    <>
      {/* Top Info Bar */}
      <div className="bg-gray-400 text-slate-800 py-2 text-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <Link 
              to="/contact" 
              className="text-slate-800 hover:text-amber-600 transition-colors duration-200 cursor-pointer font-medium"
            >
              İçmeler Mah. Göktürk Cad. No 76/A Tuzla/Istanbul/Türkiye
            </Link>
            <Link 
              to="/contact" 
              className="flex items-center space-x-2 text-slate-800 hover:text-amber-600 transition-colors duration-200 cursor-pointer font-medium"
            >
              <Phone className="h-4 w-4" />
              <span>+90 216 392 28 93 / +90 546 833 56 66</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-slate-100 text-slate-800 shadow-lg py-2 relative z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/resimler/logo1.png" 
                alt="Aymer Denizcilik Logo" 
                className="h-16 w-auto"
              />
              <span className="text-slate-800 text-xl font-bold">Aymer Safety</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'text-amber-600 bg-slate-200'
                      : 'text-slate-600 hover:text-amber-600 hover:bg-slate-200'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              {/* Search Button */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="px-4 py-2 rounded-md text-base font-medium text-slate-600 hover:text-amber-600 hover:bg-slate-200 transition-colors duration-200 flex items-center space-x-2"
              >
                <Search className="h-5 w-5" />
                <span className="hidden xl:inline">{language === 'tr' ? 'Ara' : 'Search'}</span>
              </button>
            </nav>

            {/* Language Switcher & Social Media */}
            <div className="flex items-center space-x-4">
              <div className="flex flex-col items-center space-y-2">
                <button
            onClick={toggleLanguage}
            className="flex items-center space-x-2 px-3 py-1 text-sm border border-amber-600 rounded hover:bg-amber-600 hover:text-white transition-colors duration-200"
              >
            <img
            src={language === 'tr' ? '/resimler/trk.jpg' : '/resimler/eng.png'}
          alt={language === 'tr' ? 'Türk Bayrağı' : 'İngiltere Bayrağı'}
          className="h-4 w-6 object-cover"
          />
          <span>{language === 'tr' ? 'Türkçe' : 'English'}</span>
          </button>

                
                {/* Social Media Icons */}
                <div className="flex items-center space-x-2">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-slate-500 ${social.color} transition-colors duration-200`}

                    >
                      <social.icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Mobile menu button */}
              <div className="lg:hidden">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-slate-600 hover:text-amber-600 focus:outline-none focus:text-amber-600"
                >
                  {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lg:hidden py-4 border-t border-slate-300"
            >
              <nav className="flex flex-col space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                      isActive(item.path)
                        ? 'text-amber-600 bg-slate-200'
                        : 'text-slate-600 hover:text-amber-600 hover:bg-slate-200'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                {/* Mobile Search Button */}
                <button
                  onClick={() => {
                    setIsSearchOpen(true);
                    setIsMenuOpen(false);
                  }}
                  className="px-3 py-2 rounded-md text-base font-medium text-slate-600 hover:text-amber-600 hover:bg-slate-200 transition-colors duration-200 flex items-center space-x-2"
                >
                  <Search className="h-5 w-5" />
                  <span>{language === 'tr' ? 'Ara' : 'Search'}</span>
                </button>
              </nav>
              
              {/* Mobile Social Media */}
              <div className="flex items-center justify-center space-x-4 mt-4 pt-4 border-t border-slate-300">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-slate-500 hover:text-amber-600 transition-colors duration-200`}
                  >
                    <social.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </header>
      
      {/* Search Modal */}
      <SearchBar isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Header;